import { ChangeEvent, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  usePostCompetenceGroups,
  useUpdateCompetenceGroups,
} from '@legacy/apis/competenceGroupAPI';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToastifyAlert,
} from '@legacy/components/mui-components';
import { useInitialFocus } from '@legacy/hooks/useInitialFocus';
import { z } from 'zod';

import { zodResolver } from '@hookform/resolvers/zod';
import { FormControlLabel, Switch } from '@mui/material';

import { DialogProps } from '../types';

export const NewCompetenceCategoryDialog = ({
  closeDialog,
  setRowInfo,
  rowInfo,
  isEditing,
  setIsEditing,
  ...rest
}: DialogProps) => {
  const { t } = useTranslation('systemAdministrationEmployeesCompetences');

  // Initial focus
  const initialFocusElement = useRef<HTMLInputElement | null>(null);
  useInitialFocus(initialFocusElement);

  // Form handling
  const [keepOpen, setKeepOpen] = useState(false);

  const handleKeepOpenChange = (event: ChangeEvent<HTMLInputElement>) => {
    setKeepOpen(event.target.checked);
  };

  const formSchema = z.object({
    categoryName: z.string().min(1),
  });

  const form = useForm({
    defaultValues: {
      categoryName: rowInfo?.name || '',
    },
    resolver: zodResolver(formSchema),
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = form;

  // Data
  const { mutate: postCompetenceGroup } = usePostCompetenceGroups();
  const { mutate: updateCompetenceGroup } = useUpdateCompetenceGroups();

  // Actions
  const cleanUp = (closeOnSubmit: boolean = true) => {
    if (closeOnSubmit) {
      closeDialog();
    } else {
      initialFocusElement.current?.focus();
    }
    reset();
    if (setRowInfo) setRowInfo({});
    if (setIsEditing) setIsEditing(false);
  };

  const successCreateToast = (categoryName: string) => {
    toast(
      <ToastifyAlert
        title={t('modals.CategoryCreatedToastTitle', {
          CATEGORY_NAME: categoryName,
        })}
        description={t('modals.CategoryCreatedToastDescription')}
        color="success"
        data-automation-id="CreateCompetenceCategoryDialogToastSuccess"
      />,
      { autoClose: 4000, closeButton: false },
    );
  };

  const errorCreateToast = (categoryName: string, error: string) => {
    toast(
      <ToastifyAlert
        title={`${categoryName} was not created`}
        description={`${error}`}
        color="error"
        data-automation-id="CreateCompetenceCategoryDialogToastError"
      />,
      { closeButton: true },
    );
  };

  const handleCreate = () => {
    postCompetenceGroup(
      {
        name: form.getValues('categoryName'),
      },
      {
        onSuccess: () => {
          successCreateToast(form.getValues('categoryName'));
          cleanUp();
        },
        onError: (error) => {
          errorCreateToast(form.getValues('categoryName'), `${error}`);
        },
      },
    );
  };

  const handleCreateAndNew = () => {
    postCompetenceGroup(
      {
        name: form.getValues('categoryName'),
      },
      {
        onSuccess: () => {
          successCreateToast(form.getValues('categoryName'));
          cleanUp(false);
        },
        onError: (error) => {
          errorCreateToast(form.getValues('categoryName'), `${error}`);
        },
      },
    );
  };

  const handleEdit = () => {
    updateCompetenceGroup(
      {
        id: rowInfo?.apiId,
        name: form.getValues('categoryName'),
      },
      {
        onSuccess: () => {
          toast(
            <ToastifyAlert
              title={t('modals.CategoryEditedToastTitle', {
                CATEGORY_NAME: form.getValues('categoryName'),
              })}
              description={t('modals.CategoryEditedToastDescription')}
              color="success"
              data-automation-id="EditCompetenceCategoryDialogToastSuccess"
            />,
            { autoClose: 4000, closeButton: false },
          );
          cleanUp();
        },
        onError: (error) => {
          toast(
            <ToastifyAlert
              title={t('modals.CategoryEditToastTitleError', {
                CATEGORY_NAME: form.getValues('categoryName'),
              })}
              description={`${error}`}
              color="error"
              data-automation-id="EditCompetenceCategoryDialogToastError"
            />,
            { closeButton: true },
          );
        },
      },
    );
  };

  const handleCancel = () => {
    cleanUp();
  };

  const submitHandler = () => {
    if (keepOpen) {
      return handleCreateAndNew();
    }

    if (isEditing) {
      return handleEdit();
    }

    return handleCreate();
  };

  const submitDataAutomationId = () => {
    if (keepOpen) {
      return 'CompetenceCategoryDialogButtonSaveAndNew';
    }

    if (isEditing) {
      return 'EditCompetenceCategoryDialogButtonSave';
    }

    return 'CreateCompetenceCategoryDialogButtonSave';
  };

  return (
    <Dialog
      onClose={handleCancel}
      fullWidth
      maxWidth="sm"
      data-automation-id={`${isEditing ? 'Edit' : 'Create'}CompetenceCategoryDialog`}
      {...rest}
    >
      <form onSubmit={handleSubmit(submitHandler)}>
        <DialogTitle>
          {isEditing
            ? t('modals.CategoryEditHeader')
            : t('modals.CategoryCreateHeader')}
        </DialogTitle>
        <DialogContent>
          {/* Add the box to make room for the floating label */}
          <Box sx={{ pt: 1 }}>
            <Controller
              name="categoryName"
              control={control}
              render={({ field }) => (
                <TextField
                  data-automation-id={`${isEditing ? 'Edit' : 'Create'}CompetenceCategoryInputName`}
                  label={t('modals.CategoryCreateInputNameLabel')}
                  variant="outlined"
                  ariaLabel={t('modals.CategoryCreateInputNameLabel')}
                  required
                  fullWidth
                  inputRef={initialFocusElement}
                  {...field}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {!isEditing ? (
            <FormControlLabel
              control={
                <Switch checked={keepOpen} onChange={handleKeepOpenChange} />
              }
              label={t('modals.CreateKeepOpenSwitchLabel')}
              data-automation-id="CreateCompetenceCategoryDialogSwitchKeepOpen"
              // make sure the Switch is always on the left side
              sx={{ mr: 'auto' }}
            />
          ) : null}
          <Button
            variant="outlined"
            onClick={handleCancel}
            data-automation-id={`${
              isEditing ? 'Edit' : 'Create'
            }CompetenceCategoryDialogButtonCancel`}
          >
            {t('modals.CreateButtonCancelText')}
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={!isDirty || !isValid}
            data-automation-id={submitDataAutomationId()}
          >
            {keepOpen
              ? t('modals.CreateButtonSaveAndNewText')
              : t('modals.CreateButtonSaveText')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
